import { el } from "./dom";
import type { Screen } from "./screen-manager";

export interface MenuItem {
	label: string;
	onSelect: () => void;
	/** Short explanation shown under the menu while the item is focused. */
	hint?: string;
	disabled?: boolean;
	/** Called when the item gains focus (e.g. to update a preview). */
	onFocus?: () => void;
}

export interface MenuScreenOptions {
	title: string;
	subtitle?: string;
	items: MenuItem[];
	back?: () => void;
	/** Extra content rendered between the title and the buttons. */
	content?: Node;
	className?: string;
}

/** A centred panel with a title and a vertical list of buttons. */
export function menuScreen(options: MenuScreenOptions): Screen {
	// Every item's hint sits invisibly in the same grid cell as the visible one, so the hint area is
	// always as tall as the longest hint at the current width and moving focus never resizes the
	// (centred) menu (I-020). The sizers' text is CSS `content`, not DOM text: it isn't read out or
	// matched as a second copy of the hint.
	const hintText = el("span", { class: "menu-hint-text" });
	const sizers = options.items
		.filter((item) => item.hint)
		.map((item) =>
			el("span", { class: "menu-hint-sizer", "aria-hidden": "true", "data-text": item.hint }),
		);
	const hint = el("p", { class: "menu-hint", "aria-live": "polite" }, ...sizers, hintText);
	const list = el("div", { class: "menu-list", role: "menu" });

	options.items.forEach((item, i) => {
		const button = el(
			"button",
			{
				class: "btn",
				type: "button",
				role: "menuitem",
				disabled: item.disabled,
				"data-autofocus": i === 0,
				onclick: () => item.onSelect(),
			},
			item.label,
		);
		const showHint = () => {
			hintText.textContent = item.hint ?? "";
			item.onFocus?.();
		};
		button.addEventListener("focus", showHint);
		// mousemove, not mouseenter: a menu opening under a resting cursor must not steal focus.
		button.addEventListener("mousemove", () => {
			if (document.activeElement !== button) button.focus({ preventScroll: true });
		});
		list.append(button);
	});

	const back = options.back;
	const panel = el(
		"section",
		{ class: "panel" },
		back ? touchBack(back) : null,
		el("h1", { class: "title" }, options.title),
		options.subtitle ? el("p", { class: "subtitle" }, options.subtitle) : null,
		options.content ?? null,
		list,
		hint,
		navHelp(Boolean(options.back)),
	);
	const root = el("div", { class: `screen ${options.className ?? ""}`.trim() }, panel);
	root.setAttribute("role", "dialog");
	root.setAttribute("aria-label", options.title);

	return options.back ? { el: root, back: options.back } : { el: root };
}

/** A page with free-form content and a Back button. */
export function pageScreen(title: string, content: Node, back: () => void, className = ""): Screen {
	const panel = el(
		"section",
		{ class: "panel panel-wide" },
		el("h1", { class: "title" }, title),
		content,
		el(
			"div",
			{ class: "menu-list" },
			el("button", { class: "btn", type: "button", onclick: back, "data-autofocus": true }, "Back"),
		),
		navHelp(true),
	);
	const root = el("div", { class: `screen ${className}`.trim() }, panel);
	root.setAttribute("role", "dialog");
	root.setAttribute("aria-label", title);
	return { el: root, back };
}

/** Back button for touch screens, which have no Esc key (shown via body[data-coarse]). */
function touchBack(back: () => void): HTMLElement {
	return el("button", { class: "touch-back", type: "button", onclick: back }, "‹ Back");
}

function navHelp(canGoBack: boolean): HTMLElement {
	return el(
		"p",
		{ class: "nav-help" },
		el("kbd", {}, "↑"),
		el("kbd", {}, "↓"),
		" select  ",
		el("kbd", {}, "Enter"),
		" confirm",
		canGoBack ? "  " : null,
		canGoBack ? el("kbd", {}, "Esc") : null,
		canGoBack ? " back" : null,
	);
}
